import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  Analyse,
  IssuerWithPerformanceMetrics,
  SavedTweet,
} from 'r/types';
import { captureException, showNotification } from 'r/utils';
import { buildFilterString, type FilterCondition } from 'r/atoms/utils';
import {
  fetchApiRaw,
  GET_ANALYSES_CMS,
  GET_ISSUERS,
  GET_TWEET_CMS,
  POST_ANALYSES_CMS,
} from 'r/utils/fetchApiRaw';
import { StoredAuth } from 'r/utils/Stored';

const CACHE = {
  staleTime: 5 * 60 * 1000,
  gcTime: 10 * 60 * 1000,
  refetchOnWindowFocus: false,
};

const toFilter = (conditions: Record<string, FilterCondition>) =>
  encodeURIComponent(buildFilterString(conditions));

interface Page<T> {
  data: T[];
  total: number;
}

const toPage = <T>(response: { data?: unknown; total?: number }): Page<T> => ({
  data: Array.isArray(response.data) ? (response.data as T[]) : [],
  total: response.total || 0,
});

/** Tweets of the author stored in CMS by the collector */
export function useKolTweets(kolID: string, page: number, limit: number) {
  return useQuery({
    queryKey: ['cms-kol-tweets', kolID, page, limit],
    queryFn: async () => {
      try {
        const response = await fetchApiRaw(GET_TWEET_CMS, {
          queryParams: {
            skip: String(page * limit),
            take: String(limit),
            sort: 'tweetDate:DESC',
            filter: toFilter({ authorID: kolID }),
          },
        });
        return toPage<SavedTweet>(response);
      } catch (e) {
        captureException(e);
        throw e;
      }
    },
    placeholderData: (previous) => previous,
    ...CACHE,
  });
}

/** CMS analyses of the author. Key starts with 'cms-analyses' so existing
 * analysis mutations (verify, edit, delete) invalidate it. */
export function useKolAnalyses(kolID: string, page: number, limit: number) {
  return useQuery({
    queryKey: ['cms-analyses', 'kol', kolID, page, limit],
    queryFn: async () => {
      try {
        const response = await fetchApiRaw(GET_ANALYSES_CMS, {
          queryParams: {
            skip: String(page * limit),
            take: String(limit),
            sort: 'tweetDate:DESC',
            filter: toFilter({ authorID: kolID, deletedAt: null }),
          },
        });
        return toPage<Analyse>(response);
      } catch (e) {
        captureException(e);
        throw e;
      }
    },
    placeholderData: (previous) => previous,
    ...CACHE,
  });
}

/** CMS analyses for a set of tweets, used to highlight processed tweets */
export function useAnalysesByTweetIDs(tweetIDs: string[]) {
  const key = [...tweetIDs].sort().join(',');

  return useQuery({
    queryKey: ['cms-analyses', 'by-tweets', key],
    queryFn:
      tweetIDs.length === 0 ?
        skipToken
      : async () => {
          try {
            const response = await fetchApiRaw(GET_ANALYSES_CMS, {
              queryParams: {
                take: String(tweetIDs.length * 5),
                filter: toFilter({ tweetID: tweetIDs, deletedAt: null }),
              },
            });
            return toPage<Analyse>(response);
          } catch (e) {
            captureException(e);
            throw e;
          }
        },
    ...CACHE,
  });
}

/** Author record in Sanitizer: KOL flag and performance metrics */
export function useSanitizerIssuer(twitterID: string) {
  return useQuery({
    queryKey: ['sanitizer-issuer', twitterID],
    queryFn: async () => {
      try {
        const response = await fetchApiRaw(GET_ISSUERS, {
          queryParams: {
            take: '1',
            filter: toFilter({ twitterID }),
          },
        });
        const [issuer] = toPage<IssuerWithPerformanceMetrics>(response).data;
        return issuer ?? null;
      } catch (e) {
        captureException(e);
        throw e;
      }
    },
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/** Creates a CMS analysis for a tweet that is already stored in CMS */
export function useCreateAnalysisForTweet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tweetID,
      pair,
      isPredictionUp,
    }: {
      tweetID: string;
      pair: string;
      isPredictionUp: boolean;
    }) => {
      return await fetchApiRaw(
        POST_ANALYSES_CMS,
        { body: { tweetID, pair, isPredictionUp } },
        StoredAuth.getAll().cms,
      );
    },
    onSuccess: () => {
      showNotification('success', 'Analysis saved successfully');
      queryClient.invalidateQueries({ queryKey: ['cms-analyses'] });
    },
    onError: (error) => {
      captureException(error);
      showNotification('error', 'Failed to save analysis');
    },
  });
}
